import { useState } from 'react';
import axios from 'axios';

/**
 * Custom hook for managing part documents
 * Handles document upload, download, and listing
 */
export const usePartDocuments = () => {
  const [documentDialogOpen, setDocumentDialogOpen] = useState(false);
  const [selectedPartForDocuments, setSelectedPartForDocuments] = useState(null);
  const [documentForm, setDocumentForm] = useState({
    documentType: '',
    documentSubType: '',
    description: '',
    file: null
  });
  const [documentUploading, setDocumentUploading] = useState(false);
  const [documentUploadProgress, setDocumentUploadProgress] = useState(0);
  const [documentList, setDocumentList] = useState([]);

  const openDocumentDialog = async (part) => {
    setSelectedPartForDocuments(part);
    setDocumentForm({
      documentType: '',
      documentSubType: '',
      description: '',
      file: null
    });
    setDocumentUploading(false);
    setDocumentUploadProgress(0);
    setDocumentList([]);

    try {
      const res = await axios.get(`/api/parts/${part._id}/documents`);
      setDocumentList(res.data || []);
    } catch (error) {
      console.error('Error fetching part documents:', error);
    }

    setDocumentDialogOpen(true);
  };

  const handleDocumentUpload = async () => {
    if (!selectedPartForDocuments || !documentForm.file || !documentForm.documentType) {
      alert('Please select a file and document type');
      return;
    }

    setDocumentUploading(true);
    setDocumentUploadProgress(0);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('file', documentForm.file);
      formDataToSend.append('documentType', documentForm.documentType);
      if (documentForm.documentSubType) {
        formDataToSend.append('documentSubType', documentForm.documentSubType);
      }
      if (documentForm.description) {
        formDataToSend.append('description', documentForm.description);
      }

      await axios.post(
        `/api/parts/${selectedPartForDocuments._id}/documents`,
        formDataToSend,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (progressEvent) => {
            if (!progressEvent.total) return;
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            setDocumentUploadProgress(percentCompleted);
          }
        }
      );

      const refreshed = await axios.get(`/api/parts/${selectedPartForDocuments._id}/documents`);
      setDocumentList(refreshed.data || []);

      setDocumentForm({
        documentType: '',
        documentSubType: '',
        description: '',
        file: null
      });
      alert('Document uploaded successfully');
    } catch (error) {
      console.error('Error uploading document:', error);
      alert(error.response?.data?.message || 'Error uploading document');
    } finally {
      setDocumentUploading(false);
      setDocumentUploadProgress(0);
    }
  };

  const handleDocumentDownload = async (docId, fileName) => {
    if (!selectedPartForDocuments) return;
    try {
      const response = await axios.get(
        `/api/parts/${selectedPartForDocuments._id}/documents/${docId}/download`,
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading document:', error);
      alert('Error downloading document');
    }
  };

  return {
    documentDialogOpen,
    setDocumentDialogOpen,
    selectedPartForDocuments,
    documentForm,
    setDocumentForm,
    documentUploading,
    documentUploadProgress,
    documentList,
    openDocumentDialog,
    handleDocumentUpload,
    handleDocumentDownload
  };
};
